import React, { useEffect, useState, useRef } from "react";
import { Box, Checkbox, IconButton, LinearProgress, Menu, MenuItem, TextField, Typography, Button } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Paper from "@mui/material/Paper";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteChecklistModal from "../Modals/DeleteChecklist";
import PreviewIcon from '@mui/icons-material/Preview';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import "../../css/components/Custom/ChecklistComponent.css";
import "../../css/main.css";
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';

import ChecklistSkeleton from './Skeletons/ChecklistSkeleton';
import ScrollAwareTooltip from "./ScrollAwareTooltip";

const ChecklistComponent = ({ Task, handleAddChecklist, handleSaveTask, checklists, setChecklists, editChecklistTitles, setEditChecklistTitles,
                                newChecklistTitles, setNewChecklistTitles,
                                UpdateChecklistOrder, UpdateChecklistItem, bulkUpdateChecklistOrder, UpdateChecklistTitle, DeleteChecklistItem,
                                DeleteChecklist, CreateChecklistItem, avoidMisfire, setRefreshFlag, refreshFlag, workspace_id,
                                project_id, ToggleHiddenStatusOfChecklists, ToggleHiddenStatusOfChecklistItems, newChecklistAdded, resetChecklistAdded,
                                getMoreChecklistItems, getMoreChecklistVisibleItems, updateChecklistCompletion, getMoreChecklists, hasMoreChecklists,
                                setHasMoreChecklists, totalChecklists, checklistCompletion, checklistTotal, showSnackbar, fetchAllActivityLogs, setIsDraggingParent, defaultItemsLimit, readOnly = false}) => {
    const { t } = useTranslation();
    const [, setIsDragging] = useState(false);
    const [, setCurrentlyDraggingChecklistId] = useState(null);
    const [openDeleteChecklistModal, setOpenChecklistModal] = useState(false);
    const [selectedChecklistId, setSelectedChecklistId] = useState(-1);
    const [menuState, setMenuState] = useState({ anchorEl: null, itemId: null });
    const [anchorElMap, setAnchorElMap] = useState({});

    const [checklistOffsets, setChecklistOffsets] = useState({});
    const [checklistOffset, setChecklistOffset] = useState(0);
    const [, setChecklistHasMoreItems] = useState({});
    const [, setChecklistTotalCounts] = useState({});
    const [loadingChecklistItems, setLoadingChecklistItems] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const [fullTotals, setFullTotals] = useState({
        total: checklistTotal || 0,
        completed: checklistCompletion || 0
    });


    const inputRefs = useRef({});
    const hadMoreItemsWhenHiddenRef = useRef({});

    const [lastChecklistId, setLastChecklistId] = useState(null);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const initialOffsets = {};
        const initialHasMoreItems = {};
        const initialTotalCounts = {};
        checklists.forEach(cl => {
            initialOffsets[cl.id] = cl.items.length;
            initialHasMoreItems[cl.id] = cl.has_more_items;
            initialTotalCounts[cl.id] = cl.hidden_checked_items ? cl.total_count : cl.total_count || cl.items.length;
        });
        setChecklistOffset(checklists.length);
        setChecklistOffsets(initialOffsets);
        setChecklistHasMoreItems(initialHasMoreItems);
        setChecklistTotalCounts(initialTotalCounts);
        setIsLoading(false);
    }, [checklists]);


    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (newChecklistAdded) {

            const lastChecklistElement = document.getElementById('last-checklist');
            if (lastChecklistElement) {
                lastChecklistElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
                setLastChecklistId(null);
            }
            resetChecklistAdded();
        }
    }, [newChecklistAdded, lastChecklistId]);


    const loadMoreChecklists = async () => {
        try {
            const data = await getMoreChecklists(workspace_id, project_id, Task.id, checklistOffset);
            if (data && data.results) {
                setChecklists(prev => [...prev, ...data.results]);
                setChecklistOffset(prev => prev + data.results.length);
                setHasMoreChecklists(data.next !== null);
                totalChecklists = data.count;
            }
        } catch (error) {
            console.error("Error loading more checklists", error);
        }
    };

    // Wait until a frame + ResizeObserver callbacks are done
    const waitForResizeObservers = (ms = 0) =>
        new Promise(resolve => {
            requestAnimationFrame(() => {
                setTimeout(resolve, ms);
            });
        });

    // Merge items by ID and sort by full_order
    const mergeAndSortChecklistItems = (existingItems, newItems, fullOrder = []) => {
        const itemsMap = new Map();
        [...existingItems, ...newItems].forEach(item => itemsMap.set(item.id, item));

        const mergedItems = Array.from(itemsMap.values());

        if (fullOrder.length) {
            // Sort according to full_order array of IDs
            mergedItems.sort((a, b) => {
                const indexA = fullOrder.indexOf(a.id);
                const indexB = fullOrder.indexOf(b.id);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1; // unknown items at the end
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
        }

        return mergedItems;
    };

    const loadMoreChecklistItems = async (checklistId, loadAll = false) => {
        setLoadingChecklistItems(prev => ({ ...prev, [checklistId]: true }));
        try {
            let offset = checklistOffsets[checklistId] || 0;
            let hasMore = true;
            const checklist = checklists.find(cl => cl.id === checklistId);
            const onlyVisible = checklist ? checklist.hidden_checked_items : false;

            while (hasMore) {
                const data = await getMoreChecklistItems(
                    workspace_id,
                    project_id,
                    checklistId,
                    offset,
                    onlyVisible
                );

                const newItems = data.results;
                const nextHasMore = data.next !== null;
                const newTotalCount = data.total_count;

                setChecklists(prevChecklists =>
                    prevChecklists.map(cl => {
                        if (cl.id === checklistId) {
                            const mergedItems = mergeAndSortChecklistItems(
                                cl.items,
                                newItems,
                                cl.full_order
                            );

                            return {
                                ...cl,
                                items: mergedItems,
                                has_more_items: nextHasMore,
                                total_count: newTotalCount || cl.total_count
                            };
                        }
                        return cl;
                    })
                );

                setChecklistOffsets(prev => ({
                    ...prev,
                    [checklistId]: (prev[checklistId] || 0) + newItems.length,
                }));

                setChecklistHasMoreItems(prev => ({
                    ...prev,
                    [checklistId]: nextHasMore
                }));

                offset += newItems.length;
                hasMore = nextHasMore;

                if (!loadAll) break;
                if (hasMore) await waitForResizeObservers();
            }
        } catch (error) {
            console.error("Error loading more checklist items:", error);
        } finally {
            setLoadingChecklistItems(prev => ({ ...prev, [checklistId]: false }));
        }
    };

    const handleAddChecklistItem = async (checklistId) => {
        const title = newChecklistTitles[checklistId]?.trim();
        if (!title) return;

        const newItem = await CreateChecklistItem(workspace_id, project_id, checklistId, title);

        if (typeof fetchAllActivityLogs === "function") {
            await fetchAllActivityLogs();
        }

        setChecklists(prevChecklists =>
            prevChecklists.map(cl => {
                if (cl.id === checklistId) {
                    const updatedFullOrder = [...(cl.full_order || []), newItem.id];
                    return {
                        ...cl,
                        items: mergeAndSortChecklistItems(cl.items, [newItem], updatedFullOrder),
                        total_count: cl.total_count + 1,
                        visible_count: cl.items.length,
                        full_order: updatedFullOrder
                    };
                }
                return cl;
            })
        );

        setNewChecklistTitles(prev => ({ ...prev, [checklistId]: '' }));
        setRefreshFlag(prev => !prev);
        setFullTotals(prev => {
            const newTotals = { total: prev.total + 1, completed: prev.completed };
            updateChecklistCompletion(newTotals);
            return newTotals;
        });

        inputRefs.current[checklistId]?.focus();

        const selectedChecklist = checklists.find(cl => cl.id === checklistId);
        if (selectedChecklist && selectedChecklist.items.length < (selectedChecklist.full_order?.length || 0)) {
            loadMoreChecklistItems(checklistId, true);
        }
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        setChecklists(sortChecklists(checklists));
    }, [Task]);

    const handleChecklistModalOpen = (id) => {
        setOpenChecklistModal(true);
        setSelectedChecklistId(id);
    };

    const handleDeleteChecklistModalClose = () => {
        setOpenChecklistModal(false);
    };

    const handleCloseAndDelete = () => {
        setOpenChecklistModal(false);
        handleDeleteChecklistModalClose();
        handleDeleteChecklist();
    };

    const sortChecklists = (unsortedChecklists) => {
        const sortedChecklists = unsortedChecklists.map(cl => {
            const sortedItems = cl.items.map(item => ({
                ...item,
            })).sort((a, b) => a.order - b.order);
            return {
                ...cl,
                items: sortedItems
            };
        });
        return sortedChecklists.sort((a, b) => a.order - b.order);
    };

    const handleDeleteChecklist = async () => {
        try {
            const checklistToDelete = checklists.find(cl => cl.id === selectedChecklistId);
            if (!checklistToDelete) return;

            await DeleteChecklist(workspace_id, project_id, selectedChecklistId);

            if (typeof fetchAllActivityLogs === "function") {
                await fetchAllActivityLogs();
            }

            const updatedChecklists = checklists.filter(item => item.id !== selectedChecklistId);
            setCompleted(updatedChecklists, selectedChecklistId);
            setChecklists(updatedChecklists);
            setRefreshFlag(!refreshFlag);

            setFullTotals(prev => {
                const newTotals = {
                    total: prev.total - checklistToDelete.total_count,
                    completed: prev.completed - checklistToDelete.checked_count
                };
                updateChecklistCompletion(newTotals);
                return newTotals;
            });

        } catch (error) {
            console.error("Error deleting checklist:", error);
        }
    };


    const refreshChecklistItems = async (checklistId, newLimitOverride) => {
        try {
            const data = await getMoreChecklistVisibleItems(
                workspace_id,
                project_id,
                checklistId,
                0,
                newLimitOverride
            );
            const newItems = data.results;
            const hasMoreItems = data.next !== null;

            setChecklists(prev =>
                prev.map(cl =>
                    cl.id === checklistId
                        ? { ...cl, items: newItems, has_more_items: hasMoreItems }
                        : cl
                )
            );
            setChecklistOffsets(prev => ({ ...prev, [checklistId]: newItems.length }));
        } catch (error) {
            console.error("Error refreshing checklist items:", error);
        }
    };

    const setCompleted = (updatedChecklists, checklistId) => {
        const checklist = updatedChecklists.find(item => item.id === checklistId);

        if (!checklist) return;

        setChecklists([...updatedChecklists]);
    };

    const handleToggleChecklistItem = async (checklistId, itemId, checked) => {
        setChecklists(prevChecklists =>
            prevChecklists.map(cl => {
                if (cl.id === checklistId) {
                    const updatedItems = cl.items.map(item =>
                        item.id === itemId ? { ...item, checked } : item
                    );

                    const updatedCheckedCount = checked
                        ? cl.checked_count + 1
                        : cl.checked_count - 1;

                    return {
                        ...cl,
                        items: updatedItems,
                        checked_count: updatedCheckedCount
                    };
                }
                return cl;
            })
        );

        await UpdateChecklistItem(workspace_id, project_id, checklistId, itemId, { checked })
            .catch(error => {
                console.error("Failed to update checklist item:", error);
            });

        setFullTotals(prev => ({
            total: prev.total,
            completed: checked ? prev.completed + 1 : prev.completed - 1
        }));

        updateChecklistCompletion({
            total: fullTotals.total,
            completed: checked ? fullTotals.completed + 1 : fullTotals.completed - 1
        });
        if (typeof fetchAllActivityLogs === "function") {
            await fetchAllActivityLogs();
        }
    };

    const handleDeleteChecklistItem = async (checklistId, itemId) => {
        try {
            await DeleteChecklistItem(workspace_id, project_id, itemId);

            if (typeof fetchAllActivityLogs === "function") {
                await fetchAllActivityLogs();
            }

            let wasChecked = false;
            const updatedChecklists = checklists.map(cl => {
                if (cl.id === checklistId) {
                    const updatedItems = cl.items.filter(item => item.id !== itemId);
                    const updatedFullOrder = cl.full_order.filter(id => id !== itemId);
                    const updatedTotalCount = cl.total_count - 1;
                    const updatedCheckedCount = cl.checked_count - (cl.items.find(item => item.id === itemId)?.checked ? 1 : 0);
                    return {
                        ...cl,
                        items: updatedItems,
                        full_order: updatedFullOrder,
                        total_count: updatedTotalCount,
                        checked_count: updatedCheckedCount,
                    };
                }
                return cl;
            });
            setChecklists(updatedChecklists);

            setFullTotals(prev => {
                const newTotals = {
                    total: prev.total - 1,
                    completed: wasChecked ? prev.completed - 1 : prev.completed
                };
                updateChecklistCompletion(newTotals);
                return newTotals;
            });

        } catch (error) {
            console.error("Error deleting checklist item:", error);
        }
    };


    const handleToggleHideCheckedItems = async (checklistId) => {
        const checklist = checklists.find(cl => cl.id === checklistId);
        if (!checklist) return;
        const newHiddenState = !checklist.hidden_checked_items;

        const updatedChecklists = checklists.map(cl =>
            cl.id === checklistId
                ? { ...cl, hidden_checked_items: newHiddenState }
                : cl
        );
        setChecklists(updatedChecklists);

        await ToggleHiddenStatusOfChecklistItems(workspace_id, project_id, checklistId);

        let newLimit;

        if (newHiddenState) {
            const committedOffset = checklistOffsets[checklistId] ?? checklist.items.length;
            hadMoreItemsWhenHiddenRef.current[checklistId] = {
                offset: committedOffset,
                hadMore: checklist.has_more_items,
            };
            newLimit = committedOffset;

        } else {
            const saved = hadMoreItemsWhenHiddenRef.current[checklistId];
            if (saved) {
                newLimit = saved.hadMore ? saved.offset : checklist.total_count;
                delete hadMoreItemsWhenHiddenRef.current[checklistId];
            } else {
                newLimit = checklistOffsets[checklistId] ?? defaultItemsLimit ?? 10;
            }
        }

        if (newLimit === 0) {
            newLimit = defaultItemsLimit || 10;
        }

        refreshChecklistItems(checklistId, newLimit);
    };


    const handleToggleHideChecklist = (checklistId) => {
        setChecklists(prevChecklists =>
            prevChecklists.map(cl =>
                cl.id === checklistId ? { ...cl, hidden: !cl.hidden } : cl
            )
        );

        setAnchorElMap(prevState => ({
            ...prevState,
            [checklistId]: null
        }));

        ToggleHiddenStatusOfChecklists(workspace_id, project_id, checklistId);
    };


    const getChecklistProgress = (checklist) => {
        const totalItems = checklist.total_count || 0;
        const checkedItems = checklist.checked_count || 0;
        return totalItems === 0 ? 0 : (checkedItems / totalItems) * 100;
    };

    const handleNewChecklistTitleChange = (checklistId, value) => {
        setNewChecklistTitles(prevState => ({
            ...prevState,
            [checklistId]: value
        }));
    };

    const handleEditChecklistChange = (checklistId, title) => {
        setEditChecklistTitles(prevState => ({
            ...prevState,
            [checklistId]: title
        }));
    };

    const handleEditChecklistItemChange = (checklistId, checklistItemId, title, order, changedOrder = false) => {
        const updatedChecklists = checklists.map(cl => {
            if (cl.id === checklistId) {
                const updatedItems = cl.items.map(item =>
                    item.id === checklistItemId ? { ...item, title, order } : item
                ).sort((a, b) => a.order - b.order);

                if (changedOrder) {
                    cl.items.forEach(item => {
                        if (item.id === checklistItemId) {
                            handleEditChecklistItemSave(cl.id, item.id, item.title, item.order);
                        }
                    });
                }

                return {
                    ...cl,
                    items: updatedItems
                };
            }
            return cl;
        });

        setChecklists(updatedChecklists);

    };

    const handleEditChecklistItemSave = async (checklistId, checklistItemId, title, order) => {
        await UpdateChecklistItem(workspace_id, project_id, checklistId, checklistItemId, { title, order });
        setRefreshFlag(!refreshFlag);

        if (typeof fetchAllActivityLogs === "function") {
            await fetchAllActivityLogs();
        }
    };

    const handleSaveChecklistTitle = async (checklistId) => {
        const newTitle = editChecklistTitles[checklistId];

        if (newTitle && newTitle.trim()) {
            try {
                await UpdateChecklistTitle(workspace_id, project_id, checklistId, newTitle);

                if (typeof fetchAllActivityLogs === "function") {
                    await fetchAllActivityLogs();
                }

                const updatedChecklists = checklists.map(cl =>
                    cl.id === checklistId ? { ...cl, title: newTitle } : cl
                );

                setChecklists(updatedChecklists);
                setEditChecklistTitles(prevState => {
                    const newState = { ...prevState };
                    delete newState[checklistId];
                    return newState;
                });
                setRefreshFlag(!refreshFlag);


            } catch (error) {
                console.error("Error updating checklist title:", error);
            }
        }
    };

    const handleSaveChecklistOrder = async (checklistId, order, updateChecklists = true) => {

        try {
            await UpdateChecklistOrder(workspace_id, project_id, checklistId, order);

            const updatedChecklists = checklists.map(cl =>
                cl.id === checklistId ? { ...cl, order: order } : cl
            );
            if (updateChecklists) {
                setChecklists(updatedChecklists);
            }
        } catch (error) {
            console.error("Error updating checklist title:", error);
        }
    };


    const onDragStart = (start) => {
        const { source } = start;
        setCurrentlyDraggingChecklistId(source.droppableId.replace("checklist-items-", ""));
        setIsDragging(true);

        if (setIsDraggingParent) {
            setIsDraggingParent(true);
        }
    };

    const onDragEnd = async (result) => {

        setIsDragging(false);
        setCurrentlyDraggingChecklistId(null);

        if (setIsDraggingParent) {
            setIsDraggingParent(false);
        }

        const { destination, source } = result;

        if (!result.destination) {
            return;
        }

        let updatedChecklists = [...checklists];

        if (destination.droppableId === 'checklist' && source.droppableId === 'checklist') {
            const [removedChecklist] = updatedChecklists.splice(source.index, 1);
            updatedChecklists.splice(destination.index, 0, removedChecklist);
            updatedChecklists.forEach((checklist, index) => {
                checklist.order = index;
            });
            setChecklists(updatedChecklists);
            setRefreshFlag(!refreshFlag);
            try {
                for (const checklist of updatedChecklists) {
                    await handleSaveChecklistOrder(checklist.id, checklist.order, false);
                }
            } catch (error) {
                console.error("Error updating checklist order on the backend", error);
            }
        } else {
            const sourceChecklistId = source.droppableId.replace("checklist-items-", "");
            const destChecklistId = destination.droppableId.replace("checklist-items-", "");

            const sourceChecklist = checklists.find((cl) => cl.id === sourceChecklistId);
            if (!sourceChecklist) return;

            if (sourceChecklistId === destChecklistId) {
                const fullOrder = sourceChecklist.full_order
                    ? [...sourceChecklist.full_order]
                    : sourceChecklist.items.map((item) => item.id);

                let newFullOrder;
                if (sourceChecklist.hidden_checked_items) {
                    newFullOrder = reorderFullOrder(fullOrder, sourceChecklist.items, source.index, destination.index);
                } else {
                    newFullOrder = [...fullOrder];
                    const [moved] = newFullOrder.splice(source.index, 1);
                    newFullOrder.splice(destination.index, 0, moved);
                }
                const newItems = newFullOrder
                    .map((id) => sourceChecklist.items.find((item) => item.id === id))
                    .filter((item) => item !== undefined);

                const updatedChecklist = { ...sourceChecklist, items: newItems, full_order: newFullOrder };
                const updatedChecklists = checklists.map((cl) =>
                    cl.id === sourceChecklistId ? updatedChecklist : cl
                );
                setChecklists(updatedChecklists);

                try {
                    await bulkUpdateChecklistOrder(workspace_id, project_id, sourceChecklistId, newFullOrder);
                } catch (error) {
                    console.error("Error updating checklist order on the backend", error);
                }

            } else {
                const destChecklist = checklists.find((cl) => cl.id === destChecklistId);
                if (!destChecklist) return;

                const movedItem = sourceChecklist.items[source.index];
                if (!movedItem) return;

                const newSourceItems = sourceChecklist.items.filter((_, i) => i !== source.index);
                const newSourceFullOrder = (sourceChecklist.full_order || sourceChecklist.items.map((i) => i.id))
                    .filter((id) => id !== movedItem.id);

                const newDestItems = [...destChecklist.items];
                newDestItems.splice(destination.index, 0, movedItem);
                const newDestFullOrder = destChecklist.full_order
                    ? [...destChecklist.full_order]
                    : destChecklist.items.map((i) => i.id);
                newDestFullOrder.splice(destination.index, 0, movedItem.id);

                const movedIsChecked = movedItem.checked;
                const updatedSourceChecklist = {
                    ...sourceChecklist,
                    items: newSourceItems,
                    full_order: newSourceFullOrder,
                    total_count: sourceChecklist.total_count - 1,
                    checked_count: sourceChecklist.checked_count - (movedIsChecked ? 1 : 0),
                };
                const updatedDestChecklist = {
                    ...destChecklist,
                    items: newDestItems,
                    full_order: newDestFullOrder,
                    total_count: destChecklist.total_count + 1,
                    checked_count: destChecklist.checked_count + (movedIsChecked ? 1 : 0),
                };

                const updatedChecklists = checklists.map((cl) => {
                    if (cl.id === sourceChecklistId) return updatedSourceChecklist;
                    if (cl.id === destChecklistId) return updatedDestChecklist;
                    return cl;
                });
                setChecklists(updatedChecklists);

                try {
                    await UpdateChecklistItem(workspace_id, project_id, destChecklistId, movedItem.id, {
                        checklist: destChecklistId,
                    });
                    await bulkUpdateChecklistOrder(workspace_id, project_id, sourceChecklistId, newSourceFullOrder);
                    await bulkUpdateChecklistOrder(workspace_id, project_id, destChecklistId, newDestFullOrder);
                } catch (error) {
                    console.error("Error moving checklist item across checklists", error);
                    setChecklists(checklists);
                }
            }
        }

        setIsDragging(false);
        setCurrentlyDraggingChecklistId(null);
    };


    function reorderFullOrder(fullOrder, checklistItems, sourceVisibleIndex, destinationVisibleIndex) {

        const visibleFullIndices = [];
        for (let i = 0; i < fullOrder.length; i++) {
            const item = checklistItems.find((it) => it.id === fullOrder[i]);
            if (item && !item.checked) {
                visibleFullIndices.push(i);
            }
        }

        if (!visibleFullIndices.length) {
            return fullOrder;
        }

        const sourceFullIndex = visibleFullIndices[sourceVisibleIndex];

        const newOrder = [...fullOrder];
        const [movedId] = newOrder.splice(sourceFullIndex, 1);

        const newVisibleIndices = [];
        for (let i = 0; i < newOrder.length; i++) {
            const item = checklistItems.find((it) => it.id === newOrder[i]);
            if (item && !item.checked) {
                newVisibleIndices.push(i);
            }
        }

        let destinationFullIndex;
        if (destinationVisibleIndex >= newVisibleIndices.length) {
            destinationFullIndex = newOrder.length;
        } else {
            destinationFullIndex = newVisibleIndices[destinationVisibleIndex];
        }

        newOrder.splice(destinationFullIndex, 0, movedId);

        return newOrder;
    }

    const handleClick = (event, checklistId, itemId) => {
        setMenuState({ anchorEl: event.currentTarget, checklistId, itemId });
    };

    const handleClickChecklist = (event, checklistId) => {
        setAnchorElMap((prev) => ({
            ...prev,
            [checklistId]: event.currentTarget,
        }));

    };

    const handleCloseChecklist = (checklistId) => {
        setAnchorElMap((prev) => ({
            ...prev,
            [checklistId]: null,
        }));
    };

    const handleClose = () => {
        setMenuState({ anchorEl: null, checklistId: null, itemId: null });
    };


    const handleDelete = (checklistId, itemId) => {
        if (itemId !== null) {
            handleDeleteChecklistItem(checklistId, itemId);
        }
        handleClose();
    };

    const handleConvertToTask = async (checklistId, itemTitle, itemId) => {
        await handleDeleteChecklistItem(checklistId, itemId)
        await handleSaveTask(itemTitle, []);

        handleClose();
    };

    if (isLoading) {
        return <ChecklistSkeleton />;
    }

    return (
        <>
            <DeleteChecklistModal open={openDeleteChecklistModal} handleDeleteChecklistModalClose={handleDeleteChecklistModalClose} handleCloseAndDelete={handleCloseAndDelete} avoidMisfire={avoidMisfire} />
            <Box className={`${!checklists?.length ? "opacity-box" : ""}`}>

                <div className="checklist-container">
                    <Typography variant="h6">{t("custom.checklists")}</Typography>
                    {!readOnly && (
                        <Button onClick={handleAddChecklist} className="add-button">
                            <AddIcon className="add-icon" />
                        </Button>
                    )}
                </div>

                {checklists && checklists.length === 0 && (
                    <Typography sx={{ mb: 2 }}>{t("custom.no_checklists")}</Typography>
                )}
                <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <div>
                        <Droppable droppableId="checklist" direction="vertical" type="CHECKLIST">
                            {(provided) => (
                                <div className='checklist-items-drag-div-divider' {...provided.droppableProps} ref={provided.innerRef}>
                                    {checklists.map((checklist, index) => {
                                        const isLastChecklist = index === checklists.length - 1;

                                        if (isLastChecklist && newChecklistAdded) {
                                            setLastChecklistId(checklist.id);
                                        }

                                        return (
                                            <Draggable draggableId={checklist.id.toString()} index={index} key={checklist.id} isDragDisabled={readOnly}>
                                                {(provided_parent) => (
                                                    <Paper
                                                        elevation={4}
                                                        className="checklist-paper"
                                                        {...provided_parent.draggableProps}
                                                        ref={provided_parent.innerRef}
                                                        id={index === checklists.length - 1 ? 'last-checklist' : undefined}
                                                    >
                                                        {!checklist.hidden ? (
                                                            <Droppable droppableId={`checklist-items-${checklist.id}`} type="ITEM" direction="vertical">
                                                                {(provided) => (
                                                                    <>
                                                                        <div {...provided.droppableProps} ref={provided.innerRef}>
                                                                            <Box key={checklist.id}>
                                                                                <Box className="checklist-title-container">
                                                                                    {!readOnly && (
                                                                                        <Box
                                                                                            className="checklist-drag-icon"
                                                                                            {...provided_parent.dragHandleProps}
                                                                                        >
                                                                                            <DragIndicatorIcon className="white-txt-color" />
                                                                                        </Box>
                                                                                    )}

                                                                                    <TextField
                                                                                        variant="standard"
                                                                                        value={editChecklistTitles[checklist.id] !== undefined ? editChecklistTitles[checklist.id] : checklist.title}
                                                                                        onChange={(e) => handleEditChecklistChange(checklist.id, e.target.value)}
                                                                                        onBlur={() => handleSaveChecklistTitle(checklist.id)}
                                                                                        onKeyDown={(e) => {
                                                                                            if (e.key === 'Enter') {
                                                                                                e.target.blur();
                                                                                            }
                                                                                        }}
                                                                                        className="checklist-title-input"
                                                                                        InputProps={{
                                                                                            className: "white-txt-color",
                                                                                            readOnly: readOnly
                                                                                        }}
                                                                                        multiline
                                                                                    />

                                                                                    <div className="checkbox-icons-div">
                                                                                        {!readOnly && (
                                                                                            <>
                                                                                                <IconButton onClick={(event) => handleClickChecklist(event, checklist.id)} size="small">
                                                                                                    <MoreVertIcon className="white-txt-color" />
                                                                                                </IconButton>
                                                                                                <Menu
                                                                                                    anchorEl={anchorElMap[checklist.id]}
                                                                                                    open={Boolean(anchorElMap[checklist.id])}
                                                                                                    onClose={() => handleCloseChecklist(checklist.id)}
                                                                                                    className="checklist-menu"
                                                                                                >
                                                                                                    <MenuItem onClick={() => handleToggleHideChecklist(checklist.id)} className="checklist-menu-item">
                                                                                                        <PreviewIcon className="checklist-visibility-icon" />
                                                                                                        {checklist.hidden ? t("custom.show_checked") : t("custom.hide_checked")}
                                                                                                    </MenuItem>
                                                                                                    <MenuItem onClick={() => handleToggleHideCheckedItems(checklist.id)} className="checklist-menu-item">
                                                                                                        <VisibilityIcon className="checklist-visibility-icon" />
                                                                                                        {checklist.hidden_checked_items ? t("custom.show_item_checked") : t("custom.hide_item_checked")}
                                                                                                    </MenuItem>
                                                                                                    <MenuItem onClick={() => handleChecklistModalOpen(checklist.id)} className="checklist-menu-item">
                                                                                                        <DeleteIcon className="checklist-delete-icon" />
                                                                                                        {t("custom.delete_checklist")}
                                                                                                    </MenuItem>
                                                                                                </Menu>
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                </Box>

                                                                                {checklist.items && (
                                                                                    <Typography variant="body2" className="checklist-progress-text">
                                                                                        {`${checklist.checked_count}/${checklist.total_count}`} ({`${getChecklistProgress(checklist).toFixed(2)}%`})
                                                                                    </Typography>
                                                                                )}
                                                                                {checklist.hidden_checked_items && (
                                                                                    <Typography variant="body2" className="checklist-progress-text">
                                                                                        - {t("custom.checked_items_hidden")}
                                                                                    </Typography>
                                                                                )}

                                                                                <LinearProgress
                                                                                    variant="determinate"
                                                                                    value={getChecklistProgress(checklist)}
                                                                                    className="checklist-linear-progress"
                                                                                />

                                                                                {(checklist.hidden_checked_items
                                                                                    ? checklist.items.filter(item => !item.checked)
                                                                                    : checklist.items
                                                                                ).map((item, index) => (
                                                                                    <Draggable draggableId={item.id.toString()} index={index} key={item.id} isDragDisabled={readOnly}>
                                                                                        {(provided, snapshot) => (
                                                                                            <Paper
                                                                                                elevation={4}
                                                                                                className={`checklist-small-paper ${item.checked ? 'checklist-item-checked' : ''}`}
                                                                                                {...provided.draggableProps}
                                                                                                {...(!readOnly ? provided.dragHandleProps : {})}
                                                                                                ref={provided.innerRef}
                                                                                            >
                                                                                                <Box key={item.id} className="checklist-title-container">
                                                                                                    {!readOnly && (
                                                                                                        <ScrollAwareTooltip title="Hold to drag amongst hidden checked items!">
                                                                                                            <Box className="checklist-drag-icon">
                                                                                                                <DragIndicatorIcon />
                                                                                                            </Box>
                                                                                                        </ScrollAwareTooltip>
                                                                                                    )}
                                                                                                    <Checkbox
                                                                                                        checked={item.checked}
                                                                                                        onChange={() => handleToggleChecklistItem(checklist.id, item.id, !item.checked)}
                                                                                                        className="white-txt-color"
                                                                                                        disabled={readOnly}
                                                                                                    />
                                                                                                    <TextField
                                                                                                        variant="standard"
                                                                                                        value={item.title}
                                                                                                        onChange={(e) => handleEditChecklistItemChange(checklist.id, item.id, e.target.value, item.order, false)}
                                                                                                        onKeyDown={(e) => {
                                                                                                            if (e.key === 'Enter') {
                                                                                                                e.target.blur();
                                                                                                            }
                                                                                                        }}
                                                                                                        onBlur={(e) => handleEditChecklistItemSave(checklist.id, item.id, e.target.value, item.order)}
                                                                                                        multiline
                                                                                                        className="checklist-title-input"
                                                                                                        InputProps={{
                                                                                                            className: "white-txt-color",
                                                                                                            readOnly: readOnly
                                                                                                        }}
                                                                                                    />
                                                                                                    {!readOnly && (
                                                                                                        <>
                                                                                                            <IconButton
                                                                                                                onClick={(event) => {
                                                                                                                    event.stopPropagation();
                                                                                                                    handleClick(event, checklist.id, item.id);
                                                                                                                }}
                                                                                                                size="small"
                                                                                                            >
                                                                                                                <MoreVertIcon className="white-txt-color" />
                                                                                                            </IconButton>
                                                                                                            <Menu
                                                                                                                anchorEl={menuState.anchorEl}
                                                                                                                open={menuState.checklistId === checklist.id && menuState.itemId === item.id}
                                                                                                                onClose={handleClose}
                                                                                                                className="checklist-menu"
                                                                                                            >
                                                                                                                <MenuItem onClick={() => handleDelete(checklist.id, item.id)}>
                                                                                                                    {t("custom.delete_item")}
                                                                                                                </MenuItem>
                                                                                                                <MenuItem onClick={() => handleConvertToTask(checklist.id, item.title, item.id)}>
                                                                                                                    {t("custom.convert_item")}
                                                                                                                </MenuItem>
                                                                                                            </Menu>
                                                                                                        </>
                                                                                                    )}
                                                                                                </Box>
                                                                                            </Paper>
                                                                                        )}
                                                                                    </Draggable>
                                                                                ))}
                                                                            </Box>
                                                                            {provided.placeholder}
                                                                        </div>

                                                                        {!readOnly && (
                                                                            <Box className="checklist-add-item">
                                                                                <TextField
                                                                                    size="small"
                                                                                    placeholder={t("custom.add_checklist_item")}
                                                                                    value={newChecklistTitles[checklist.id] || ''}
                                                                                    onChange={(e) => handleNewChecklistTitleChange(checklist.id, e.target.value)}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === 'Enter') {
                                                                                            handleAddChecklistItem(checklist.id);
                                                                                            e.target.blur();
                                                                                        }
                                                                                    }}
                                                                                    inputRef={(el) => (inputRefs.current[checklist.id] = el)}
                                                                                    className="checklist-new-item-input"
                                                                                    InputProps={{
                                                                                        className: "white-txt-color"
                                                                                    }}
                                                                                    multiline
                                                                                />
                                                                                <IconButton onClick={() => handleAddChecklistItem(checklist.id)} size="small">
                                                                                    <AddIcon className="white-txt-color" />
                                                                                </IconButton>
                                                                            </Box>
                                                                        )}

                                                                        {loadingChecklistItems[checklist.id] ? (
                                                                            <Typography variant="body2" className="center-loading-checklists-items">{t("custom.loading_checklists")} <TinyCustomFallback /></Typography>
                                                                        ) : (
                                                                            checklist.has_more_items && (
                                                                                <Box className="load-more-container">
                                                                                    <Button
                                                                                        variant="contained"
                                                                                        onClick={() => loadMoreChecklistItems(checklist.id)}
                                                                                        className="load-more-button"
                                                                                    >
                                                                                        {t("custom.load_more_items")}
                                                                                    </Button>
                                                                                    <Typography variant="body2" sx={{ m: 1, display: "flex", justifyContent: "center" }}>
                                                                                        {`${
                                                                                            (checklist.hidden_checked_items
                                                                                                ? (checklist.total_count - checklist.checked_count)
                                                                                                : checklist.total_count)
                                                                                            - checklist.items.length
                                                                                        } ${t("custom.items_missing")}`}
                                                                                    </Typography>
                                                                                    <Button
                                                                                        variant="contained"
                                                                                        onClick={() => loadMoreChecklistItems(checklist.id, true)}
                                                                                        className="load-more-button"
                                                                                        disabled={loadingChecklistItems[checklist.id]}
                                                                                    >
                                                                                        {t("custom.load_all_items")}
                                                                                    </Button>
                                                                                </Box>
                                                                            )
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Droppable>
                                                        ) : (
                                                            <Droppable droppableId={`checklist-items-${checklist.id}`} type="ITEM" direction="vertical">
                                                                {(provided) => (
                                                                    <div className='checklist-items-drag-div-divider' {...provided.droppableProps} ref={provided.innerRef}>
                                                                        <Box key={checklist.id}>
                                                                            <Box className="checklist-title-container">
                                                                                {!readOnly && (
                                                                                    <Box
                                                                                        className="checklist-drag-icon"
                                                                                        {...provided_parent.dragHandleProps}
                                                                                    >
                                                                                        <DragIndicatorIcon className="white-txt-color" />
                                                                                    </Box>
                                                                                )}

                                                                                <TextField
                                                                                    variant="standard"
                                                                                    value={editChecklistTitles[checklist.id] !== undefined ? editChecklistTitles[checklist.id] : checklist.title}
                                                                                    onChange={(e) => handleEditChecklistChange(checklist.id, e.target.value)}
                                                                                    onBlur={() => handleSaveChecklistTitle(checklist.id)}
                                                                                    disabled={readOnly}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === 'Enter') {
                                                                                            e.target.blur();
                                                                                        }
                                                                                    }}
                                                                                    className="checklist-title-input"
                                                                                    InputProps={{
                                                                                        className: "white-txt-color",
                                                                                        readOnly: readOnly
                                                                                    }}
                                                                                    multiline
                                                                                />

                                                                                <div className="checkbox-icons-div">
                                                                                    {!readOnly && (
                                                                                        <>
                                                                                            <IconButton onClick={(event) => handleClickChecklist(event, checklist.id)} size="small">
                                                                                                <MoreVertIcon className="white-txt-color" />
                                                                                            </IconButton>
                                                                                            <Menu
                                                                                                anchorEl={anchorElMap[checklist.id]}
                                                                                                open={Boolean(anchorElMap[checklist.id])}
                                                                                                onClose={() => handleCloseChecklist(checklist.id)}
                                                                                                className="checklist-menu"
                                                                                            >
                                                                                                <MenuItem onClick={() => handleToggleHideChecklist(checklist.id)} className="checklist-menu-item">
                                                                                                    <PreviewIcon className="checklist-visibility-icon" />
                                                                                                    {checklist.hidden ? t("custom.show_checked") : t("custom.hide_checked")}
                                                                                                </MenuItem>
                                                                                                <MenuItem onClick={() => handleToggleHideCheckedItems(checklist.id)} className="checklist-menu-item">
                                                                                                    <VisibilityIcon className="checklist-visibility-icon" />
                                                                                                    {checklist.hidden_checked_items ? t("custom.show_item_checked") : t("custom.hide_item_checked")}
                                                                                                </MenuItem>
                                                                                                <MenuItem onClick={() => handleChecklistModalOpen(checklist.id)} className="checklist-menu-item">
                                                                                                    <DeleteIcon className="checklist-delete-icon" />
                                                                                                    {t("custom.delete_checklist")}
                                                                                                </MenuItem>
                                                                                            </Menu>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </Box>

                                                                            <div className="check_items_text">
                                                                                {checklist.items && (
                                                                                    <Typography variant="body2" className="checklist-progress-text">
                                                                                        {`${checklist.checked_count}/${checklist.total_count}`} ({`${getChecklistProgress(checklist).toFixed(2)}%`})
                                                                                    </Typography>
                                                                                )}
                                                                                {checklist.hidden_checked_items && (
                                                                                    <Typography variant="body2" className="checklist-progress-text">
                                                                                        - {t("custom.checked_items_hidden")}
                                                                                    </Typography>
                                                                                )}
                                                                            </div>

                                                                            <LinearProgress
                                                                                variant="determinate"
                                                                                value={getChecklistProgress(checklist)}
                                                                                className="checklist-linear-progress"
                                                                            />
                                                                        </Box>
                                                                        {provided.placeholder}
                                                                    </div>
                                                                )}
                                                            </Droppable>
                                                        )}
                                                    </Paper>
                                                )}
                                            </Draggable>
                                        )
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                        {hasMoreChecklists && (
                            <Box className="load-more-checklists-container" sx={{ mt: 2, textAlign: 'center' }}>
                                <Button variant="contained" onClick={loadMoreChecklists}>
                                    {t("custom.load_more_checklists")}
                                </Button>
                            </Box>
                        )}
                    </div>
                </DragDropContext>
            </Box>
        </>
    );
}

export default ChecklistComponent;